import { CookiesTester } from '../../components/BrowserSupport/CookiesTester/CookiesTester';
import { getOrCreateFeatureFlag } from '../../config/FeatureFlag';
import { NotificationProvider } from '../../contexts/Notification.context';
import { globalStyleWithCssVar } from '../../theme/globalStyle';
import { getOrCreateAnalytics } from '../../utils/analytics';
import { NavinaDataProvider, useNavinaData } from '../../utils/useNavinaData';
import { ForeverSpinnerLayout } from './ForeverSpinnerLayout';
import { Global } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import QueryString from 'query-string';
import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useMatches, type UIMatch } from 'react-router';

function hasPageHandle(match: UIMatch): match is UIMatch<unknown, { readonly page: string }> {
	return (
		typeof match.handle === 'object' &&
		match.handle !== null &&
		'page' in match.handle &&
		typeof match.handle.page === 'string'
	);
}

export const RootLayout = observer(function RootLayout() {
	const location = useLocation();
	const analytics = getOrCreateAnalytics();
	const { screenLayout } = useNavinaData();
	const matches = useMatches();

	const firstMatchWithPageHandle = matches.findLast(hasPageHandle);
	const maybeCurrentPage = firstMatchWithPageHandle?.handle.page ?? null;

	const featureFlag = getOrCreateFeatureFlag();
	const { flagsAreInitialized } = featureFlag;

	useEffect((): void => {
		const parsedQueries = QueryString.parse(globalThis.location.search);

		const src = Array.isArray(parsedQueries.src) ? parsedQueries.src[0] : parsedQueries.src;
		const openedAs = Array.isArray(parsedQueries.openedAs) ? parsedQueries.openedAs[0] : parsedQueries.openedAs;

		analytics.setAnalyticTrack({
			fullScreen: !globalThis.location.href.includes('overlay'),
			...(src && { src }),
			...(openedAs && { openedAs }),
		});
	}, [location]);

	useEffect(
		function updateFeatureFlagContext(): void {
			if (!flagsAreInitialized) {
				return;
			}

			featureFlag.extendUserContext({
				screen_layout: screenLayout,
				page: maybeCurrentPage,
			});
		},
		[screenLayout, maybeCurrentPage, flagsAreInitialized],
	);

	return (
		<>
			<CookiesTester />
			<Global styles={globalStyleWithCssVar} />
			<NavinaDataProvider>
				<NotificationProvider>
					<Suspense fallback={<ForeverSpinnerLayout />}>
						<Outlet />
					</Suspense>
				</NotificationProvider>
			</NavinaDataProvider>
		</>
	);
});
